import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { ConnectivityService } from '@app/core/connectivity/connectivity.service';
import { NotificationService } from '@app/core/notifications/notification.service';
import { ENVIRONMENT } from '@env/environment.model';
import { SKIP_CONNECTION_LOST_NOTICE } from './http-context.tokens';
import { isConnectionError } from './http-error';
import { isApiRequest } from './is-api-request';

/**
 * Una request a la API que no llegó al servidor (status 0) avisa 'connection-lost', una sola vez.
 * - Sin conexión del navegador no avisa: ya está el aviso persistente de `app-connection-status`.
 * - Con un 'connection-lost' a la vista no apila otro, aunque fallen varias requests a la vez.
 * - Una request marcada con `SKIP_CONNECTION_LOST_NOTICE` no avisa: es de fondo y reintenta sola.
 * El error se re-lanza siempre: cada caller decide qué hacer con su flujo (finalize, colas, redirecciones).
 */
export const connectionErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const notifications = inject(NotificationService);
  const connectivity = inject(ConnectivityService);
  const apiUrl = inject(ENVIRONMENT).apiUrl;

  return next(req).pipe(
    catchError((error: unknown) => {
      if (
        isConnectionError(error) &&
        isApiRequest(req, apiUrl) &&
        !req.context.get(SKIP_CONNECTION_LOST_NOTICE) &&
        connectivity.isOnline() &&
        !notifications.has('connection-lost')
      ) {
        notifications.notify({ code: 'connection-lost' });
      }
      return throwError(() => error);
    }),
  );
};
